import { Editor, Node, Range, Element as SlateElement, Transforms, } from 'slate';
import isURL from 'validator/lib/isURL';
/** Remove the currently selected link element from the document */
export function removeLink(editor) {
    Transforms.unwrapNodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
    });
}
/**
 * Slate.js editor plugin that add support of link elements Creates link
 * elements when a url is pasted into the editor
 */
export function withLinks(editor) {
    const { insertData, insertText, isInline, normalizeNode } = editor;
    editor.isInline = (element) => {
        return element.type === 'link' ? true : isInline(element);
    };
    editor.insertText = (text) => {
        if (isURL(text, { require_protocol: true })) {
            insertLink(editor, text, text);
        }
        else {
            insertText(text);
        }
    };
    editor.normalizeNode = (entry) => {
        const [node, path] = entry;
        if (SlateElement.isElement(node) && node.type === 'paragraph') {
            const children = Array.from(Node.children(editor, path));
            for (const [child, childPath] of children) {
                // Remove link nodes whose text value is empty string.
                // Empty text links happen when moving from link to next line or deleting link line.
                if (getLinkText(child) === '') {
                    if (children.length === 1) {
                        Transforms.removeNodes(editor, { at: path });
                        Transforms.insertNodes(editor, {
                            type: 'paragraph',
                            children: [{ text: '' }],
                        });
                    }
                    else {
                        Transforms.removeNodes(editor, { at: childPath });
                    }
                    return;
                }
            }
        }
        normalizeNode(entry);
    };
    editor.insertData = (data) => {
        const text = data.getData('text/plain');
        if (isURL(text, { require_protocol: true })) {
            insertLink(editor, text, text);
        }
        else {
            insertData(data);
        }
    };
    return editor;
}
/**
 * Retrieves text attached to <a> element
 *
 * @param element Slate node element
 * @returns String text element of a link
 */
export function getLinkText(element) {
    var _a;
    return SlateElement.isElement(element) && element.type === 'link'
        ? (_a = element.children[0]) === null || _a === void 0 ? void 0 : _a.text
        : undefined;
}
/**
 * Create and insert a link into the document. If a link is currently selected
 * it is converted into a text node first so we don't get a double link. If
 * there is no selection currently the link is appended to the end of the
 * document.
 *
 * @param editor Slate editor instance
 * @param url Url the link should point to
 * @param text Text to be added inside the link
 */
export function insertLink(editor, url, text) {
    const { selection } = editor;
    if (isLinkActive(editor)) {
        removeLink(editor);
    }
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
        type: 'link',
        href: url,
        children: isCollapsed ? [{ text }] : [],
    };
    if (isCollapsed) {
        Transforms.insertNodes(editor, link);
    }
    else {
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: 'end' });
    }
}
/**
 * Returns true if a link is currently selected in the editor
 *
 * @param editor Slate editor instance
 * @returns Boolean
 */
export function isLinkActive(editor) {
    const linkEntries = Array.from(Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
    }));
    return linkEntries.length > 0;
}
/**
 * Find and return the currently selected link element Returns undefined if no
 * link is currently selected.
 *
 * @param editor Slate.js editor instance
 * @returns Link element or undefined
 */
export function getLink(editor) {
    const linkEntries = Array.from(Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === 'link',
    }));
    if (linkEntries.length === 0)
        return undefined;
    const node = linkEntries[0][0];
    return SlateElement.isElement(node) && node.type === 'link'
        ? node
        : undefined;
}
